/**
 * Secure Azure Blob Storage Service
 * Handles image/file uploads with backend-generated SAS tokens
 *
 * Security Features:
 * - No storage credentials in frontend
 * - Backend-generated short-lived SAS tokens
 * - Blob path storage (not full URLs)
 * - Server-side access control
 */

import { apiClient } from '../../api/client';

export interface UploadProgress {
  percentage: number;
  loaded: number;
  total: number;
}

export interface FileValidation {
  valid: boolean;
  error?: string;
}

export interface UploadUrlRequest {
  shopId: string;
  type: 'catalog' | 'shop' | 'website';
  productId?: string;
  fileName: string;
}

export interface UploadUrlResponse {
  uploadUrl: string;
  blobPath: string;
}

export interface ReadUrlResponse {
  readUrl: string;
}

class StorageService {
  // File validation
  validateFile(file: File, maxSizeMB: number = 5): FileValidation {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = maxSizeMB * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Only JPG, PNG, and WebP images are allowed'
      };
    }

    if (file.size > maxSize) {
      return {
        valid: false,
        error: `File size must be less than ${maxSizeMB}MB`
      };
    }

    return { valid: true };
  }

  // Get upload URL from backend
  async getUploadUrl(request: UploadUrlRequest): Promise<UploadUrlResponse> {
    console.log('🔐 [Storage] Requesting secure upload URL:', request);

    const response = await apiClient.post<{ data: UploadUrlResponse; success: boolean; message: string }>('/api/storage/upload-url', request);

    console.log('✅ [Storage] Upload URL received:', response.data);

    // Handle wrapped response format
    if (response.data && response.data.data) {
      return response.data.data;
    }

    // Fallback to direct response.data if not wrapped
    return response.data as any;
  }

  // Get read URL from backend
  async getReadUrl(blobPath: string): Promise<string> {
    if (!blobPath) {
      console.error('❌ [Storage] No blobPath provided');
      throw new Error('No blob path provided');
    }

    console.log('🔐 [Storage] Requesting read URL for:', blobPath);

    try {
      const response = await apiClient.get<{ data: ReadUrlResponse; success: boolean; message: string }>(`/api/storage/read-url?blobPath=${encodeURIComponent(blobPath)}`);

      console.log('📡 [Storage] Read URL API response:', response.data);

      // Handle wrapped response format: { data: { readUrl: "..." }, success: true }
      if (response.data && response.data.data && response.data.data.readUrl) {
        console.log('✅ [Storage] Read URL received from data.data.readUrl');
        return response.data.data.readUrl;
      }

      // Fallback: check if readUrl is directly in response.data
      if (response.data && (response.data as any).readUrl) {
        console.log('✅ [Storage] Read URL received from data.readUrl');
        return (response.data as any).readUrl;
      }

      // Additional check: sometimes backend returns nested structure
      if (response.data && (response.data as any).data && (response.data as any).data.data && (response.data as any).data.data.readUrl) {
        console.log('✅ [Storage] Read URL received from nested data.data.data.readUrl');
        return (response.data as any).data.data.readUrl;
      }

      console.error('❌ [Storage] Invalid read URL response structure:', JSON.stringify(response.data, null, 2));
      throw new Error('Invalid response: missing readUrl in expected locations');
    } catch (error) {
      console.error('❌ [Storage] Failed to get read URL:', error);
      throw new Error(`Failed to get read URL from server: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Upload file to Azure Blob using SAS URL
  async uploadToBlob(
    uploadUrl: string,
    file: File,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress: UploadProgress = {
              percentage: Math.round((event.loaded / event.total) * 100),
              loaded: event.loaded,
              total: event.total
            };
            onProgress(progress);
          }
        });
      }

      // Handle completion
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          console.log('✅ [Storage] File uploaded successfully');
          resolve();
        } else {
          console.error('❌ [Storage] Upload failed:', xhr.statusText);
          reject(new Error(`Upload failed: ${xhr.statusText}`));
        }
      });

      // Handle errors
      xhr.addEventListener('error', () => {
        console.error('❌ [Storage] Upload error');
        reject(new Error('Network error during upload'));
      });

      // Handle timeout
      xhr.addEventListener('timeout', () => {
        console.error('❌ [Storage] Upload timeout');
        reject(new Error('Upload timeout'));
      });

      // Set up the request
      xhr.open('PUT', uploadUrl, true);
      xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.timeout = 60000; // 60 second timeout

      // Start upload
      console.log('📤 [Storage] Starting file upload...');
      xhr.send(file);
    });
  }

  // Alias for uploadToBlob for compatibility
  async uploadFile(
    uploadUrl: string,
    file: File,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<void> {
    return this.uploadToBlob(uploadUrl, file, onProgress);
  }

  // Complete upload process: get URL, upload file, return blobPath
  async uploadImage(
    file: File,
    shopId: string,
    type: 'catalog' | 'shop' | 'website',
    productId?: string,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<string> {
    try {
      // Generate unique product ID if not provided
      const finalProductId = productId || this.generateUUID();

      console.log('📦 [Storage] Upload request:', {
        fileName: file.name,
        shopId,
        type,
        productId: finalProductId,
        fileSize: file.size,
        fileType: file.type
      });

      // Get secure upload URL from backend
      const uploadResponse = await this.getUploadUrl({
        shopId,
        type,
        productId: finalProductId,
        fileName: file.name
      });

      console.log('📦 [Storage] Upload response received:', {
        hasUploadUrl: !!uploadResponse.uploadUrl,
        uploadUrlPreview: uploadResponse.uploadUrl ? uploadResponse.uploadUrl.substring(0, 100) + '...' : 'MISSING',
        blobPath: uploadResponse.blobPath
      });

      if (!uploadResponse.uploadUrl) {
        throw new Error('Upload URL is undefined or null');
      }

      if (!uploadResponse.blobPath) {
        throw new Error('Blob path is undefined or null');
      }

      const { uploadUrl, blobPath } = uploadResponse;

      // Upload file to Azure Blob
      console.log('📤 [Storage] Starting blob upload to:', uploadUrl.substring(0, 50) + '...');
      await this.uploadToBlob(uploadUrl, file, onProgress);

      console.log('✅ [Storage] Complete upload process finished, blobPath:', blobPath);
      return blobPath;

    } catch (error) {
      console.error('❌ [Storage] Upload process failed:', error);
      throw error;
    }
  }

  // Generate UUID for product ID
  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}

// Export both the class and a singleton instance
export { StorageService };
export const storageService = new StorageService();

